import {
  Box,
  FormControl,
  MenuItem,
  Select,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useProposalGenerationStore } from "../store/ProposalGenerationStore";
import { useGetStageConfig } from "../services/ProposalGenerationConfigService";
import { useReportTemplateList } from "src/views/reports/ReportServices";
import { ReportType } from "src/interfaces/enums";
import AddIcon from "@mui/icons-material/Add";
import ArticleIcon from "@mui/icons-material/Article";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import {
  resolveTemplateLink,
  type ResolvedTemplateLink,
} from "./sidebar/ReportTemplateTab";

type TemplateSelectorProps = {
  onCreateNew: () => void;
};

const TemplateSelector = ({ onCreateNew }: TemplateSelectorProps) => {
  const { selectedTemplate, setSelectedTemplate, sequenceNumber, reportType } =
    useProposalGenerationStore();
  const { data: stageConfig } = useGetStageConfig();

  // Use existing ReportServices hooks
  const {
    data: templates,
    isLoading,
    error,
  } = useReportTemplateList(ReportType.GENERATIVE);

  // Resolved link for the selected template (same logic as ReportTemplateTab)
  const [templateLink, setTemplateLink] = useState<ResolvedTemplateLink | null>(
    null,
  );
  const [linkLoading, setLinkLoading] = useState(false);

  // Resolve template artifact → viewable document link whenever selection changes
  useEffect(() => {
    if (!selectedTemplate?.artifact_fk) {
      setTemplateLink(null);
      setLinkLoading(false);
      return;
    }

    let isMounted = true;
    setLinkLoading(true);

    resolveTemplateLink(
      selectedTemplate.artifact_fk,
      selectedTemplate.dataset_fk,
    )
      .then((resolved) => {
        if (isMounted) {
          setTemplateLink(resolved);
          setLinkLoading(false);
        }
      })
      .catch(() => {
        if (isMounted) {
          setTemplateLink(null);
          setLinkLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, [selectedTemplate?.artifact_fk, selectedTemplate?.dataset_fk]);

  // Track which sequenceNumber we've already auto-selected for, persisted
  // across re-renders via a ref that's only reset when sequenceNumber changes.
  const autoSelectedForSequenceRef = useRef<number | null>(null);
  const isFirstRenderRef = useRef(true);

  // Derive the expected template name from the stage's reportType and sequenceNumber.
  // The generated-stage prefix and display suffix are configurable; the legacy
  // "discipline_report_" prefix and "discipline report" name are always recognized.
  const expectedTemplateName = useMemo(() => {
    const stageName = (
      stageConfig?.effective.stage_report_suffix_label || "discipline report"
    ).toLowerCase();
    const configuredPrefix = stageConfig?.effective.report_type_prefix;

    if (!reportType) {
      return sequenceNumber === 1 ? "review report" : stageName;
    }
    if (reportType === "review report" || reportType === "review_report") {
      return "review report";
    }
    if (
      (configuredPrefix && reportType.startsWith(configuredPrefix)) ||
      reportType.startsWith("discipline_report_")
    ) {
      return stageName;
    }
    // Fallback: replace underscores with spaces and lowercase
    return reportType.replace(/_/g, " ").toLowerCase();
  }, [reportType, sequenceNumber, stageConfig]);

  // When the stage (sequenceNumber) changes, clear the selected template and
  // auto-selection tracking so auto-selection kicks in for the new stage.
  // Skip initial mount so we don't wipe a template already set by TemplateCreator.
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }
    autoSelectedForSequenceRef.current = null;
    setSelectedTemplate(null);
  }, [sequenceNumber, setSelectedTemplate]);

  // Auto-select template based on name/stage when first arriving at the page
  // or when navigating between stages. Skipped if a template is already selected
  // (either by the user, by TemplateCreator, or by stage initialization).
  useEffect(() => {
    if (!templates?.length || sequenceNumber == null) return;
    if (selectedTemplate !== null) return;
    if (autoSelectedForSequenceRef.current === sequenceNumber) return;

    const match = templates.find((t: any) =>
      t.name?.toLowerCase().includes(expectedTemplateName),
    );

    if (match) {
      autoSelectedForSequenceRef.current = sequenceNumber;
      setSelectedTemplate(match);
    }
  }, [
    templates,
    sequenceNumber,
    setSelectedTemplate,
    selectedTemplate,
    expectedTemplateName,
  ]);

  const handleTemplateChange = useCallback(
    (event: any) => {
      const templateId = event.target.value;
      if (templateId === "create_new") {
        onCreateNew();
      } else {
        const template = templates?.find((t: any) => t.id === templateId);
        setSelectedTemplate(template || null);
      }
    },
    [templates, setSelectedTemplate, onCreateNew],
  );

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          minHeight: 200,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ textAlign: "center", p: 3 }}>
        <Typography color="error">Error loading templates</Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          minHeight: 200,
        }}
      >
        <FormControl fullWidth>
          <Select
            value={selectedTemplate?.id || ""}
            onChange={handleTemplateChange}
            displayEmpty
            inputProps={{ "data-testid": "pg-template-select" }}
            data-attr="proposal-gen:select:template"
            renderValue={() => {
              if (!selectedTemplate?.id) {
                return (
                  <Typography color="text.secondary">
                    Select a template
                  </Typography>
                );
              }
              return (
                <Stack direction="row" alignItems="center" spacing={1}>
                  <ArticleIcon fontSize="small" />
                  <Typography>{selectedTemplate?.name}</Typography>
                </Stack>
              );
            }}
          >
            <MenuItem
              value="create_new"
              data-testid="pg-template-option-create-new"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <AddIcon fontSize="small" />
                <Typography>Create New Template</Typography>
              </Stack>
            </MenuItem>
            {templates?.map((template: any) => (
              <MenuItem
                key={template.id}
                value={template.id}
                data-testid={`pg-template-option-${template.id}`}
              >
                <Stack direction="column" spacing={0.5}>
                  <Typography variant="body1">{template.name}</Typography>
                  {template.description && (
                    <Typography variant="body2" color="text.secondary">
                      {template.description}
                    </Typography>
                  )}
                </Stack>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedTemplate && (
          <Box
            data-testid="pg-template-preview"
            onClick={() => {
              if (templateLink) {
                window.open(templateLink.url, "_blank", "noopener,noreferrer");
              }
            }}
            sx={{
              mt: 3,
              p: 2,
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              backgroundColor: "background.default",
              cursor: templateLink ? "pointer" : "default",
              transition: "background-color 0.2s",
              "&:hover": templateLink
                ? {
                    backgroundColor: "action.hover",
                    "& .open-icon": { opacity: 1 },
                  }
                : {},
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Typography variant="subtitle2">Selected Template</Typography>
              {linkLoading ? (
                <CircularProgress size={14} />
              ) : (
                templateLink && (
                  <OpenInNewIcon
                    className="open-icon"
                    sx={{
                      fontSize: 16,
                      color: "text.secondary",
                      opacity: 0,
                      transition: "opacity 0.2s",
                    }}
                  />
                )
              )}
            </Stack>
            <Typography variant="body1" fontWeight={500}>
              {selectedTemplate.name}
            </Typography>
            {selectedTemplate.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {selectedTemplate.description}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </>
  );
};

export default TemplateSelector;
